import React, { useState } from "react";
import { Box } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { useAuthenticatedFileUrl } from "../../../../../hooks/useAuthenticatedFileUrl";
import { isAchievementBadgeImageUrl } from "../../../../../helpers/achievementImage";
import { ReactionAndFavorite } from "../../reaction-and-favorite/reaction-and-favorite";
import { FullScreenImage } from "../full-screen-image/full-screen-image";

interface ImageMessageProps {
  src: string;
  alt?: string;
  isSystem?: boolean;
  currentReaction?: string;
  isSaved?: boolean;
  markedIds: Record<string, string>;
  handleReactionClick: (reactionKey: string) => void;
  handleAddToFavorite: () => void;
  handleRemoveTag: (tag: string) => Promise<void>;
  isTags?: string[];
  fillContainer?: boolean;
  reactionBg?: string;
  imgSx?: SxProps<Theme>;
}

export const ImageMessage = ({
  src,
  alt,
  isSystem,
  currentReaction,
  isSaved,
  isTags,
  handleReactionClick,
  handleRemoveTag,
  handleAddToFavorite,
  markedIds,
  fillContainer = false,
  reactionBg = "#fff",
  imgSx,
}: ImageMessageProps) => {
  const { url: resolvedSrc } = useAuthenticatedFileUrl(src);
  const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const isLoaderGif = /(?:^|\/)(?:animation)?loading2\.gif(?:$|\?)/i.test(src);
  const shouldAlignAboveAvatar = isLoaderGif;

  const handleImageClick = () => {
    if (isSystem) return;

    setIsFullScreenOpen(true);
  };

  const handleCloseFullScreen = () => {
    setIsFullScreenOpen(false);
  };

  const handleImageLoad = () => {
    setIsImageLoaded(true);
    setTimeout(() => {
      const container = document.querySelector(".messages-container-chat");
      if (container) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: "smooth",
        });
      }
    }, 100);
  };

  return (
    <>
      <Box
        sx={{
          width: "100%",
          maxWidth: fillContainer
            ? "100%"
            : isLoaderGif
              ? { xs: 160, sm: 180, md: 200 }
              : { xs: 300, md: 400, lg: 500, xl: 500 },
        }}
      >
        <Box
          component="img"
          src={resolvedSrc}
          alt={alt || "Uploaded image"}
          sx={
            [
              {
                width: isLoaderGif ? "auto" : "100%",
                maxWidth: isLoaderGif ? "100%" : undefined,
                height: "auto",
                maxHeight: isLoaderGif
                  ? { xs: 160, sm: 180, md: 200 }
                  : undefined,
                borderRadius: 1,
                cursor: "pointer",
                opacity: isImageLoaded ? 1 : 0.7,
                transition: "opacity 0.3s ease",
                display: "block",
                mx: shouldAlignAboveAvatar ? 0 : isLoaderGif ? "auto" : 0,
                transform: shouldAlignAboveAvatar ? "translateX(7px)" : "none",
              },
              ...(imgSx ? (Array.isArray(imgSx) ? imgSx : [imgSx]) : []),
            ] as SxProps<Theme>
          }
          onClick={handleImageClick}
          onLoad={handleImageLoad}
        />
        <ReactionAndFavorite
          currentReaction={currentReaction}
          markedIds={markedIds}
          isSaved={isSaved}
          isTags={isTags}
          handleRemoveTag={handleRemoveTag}
          handleReactionClick={handleReactionClick}
          handleAddToFavorite={handleAddToFavorite}
          reactionBg={reactionBg}
        />
      </Box>

      <FullScreenImage
        open={isFullScreenOpen}
        onClose={handleCloseFullScreen}
        src={src}
        alt={alt}
        enableDrawing={!isAchievementBadgeImageUrl(src)}
      />
    </>
  );
};
